import React, { useState, useEffect, useCallback } from 'react';
import { executiveAPI, stationsAPI } from '../lib/api';
import { GlassCard, KpiCard, LogoLockup, EnforcedText, BadgeMadeInBharat } from '../components/shared';
import SummarySection from '../components/shared/SummarySection';
import LoadingSpinner from '../components/LoadingSpinner';
import { StatusBadge, DashboardSkeleton } from '../components/accessibility';
import PageHeader from '../components/PageHeader';
import OpsHealthStrip from '../components/OpsHealthStrip';
import DataTypeChip from '../components/DataTypeChip';
import SEOHead from '../components/SEOHead';
import { MobileExecutiveView } from '../components/mobile';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { cn } from '../lib/utils';
import { 
  Download, 
  FileText,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Activity,
  Zap,
  DollarSign,
  Clock,
  AlertTriangle,
  Users,
  MapPin,
  Gauge,
  Wallet,
  BarChart3,
  Timer
} from 'lucide-react';

/**
 * Executive Dashboard - Redesigned with URGA Design System
 * Features:
 * - Z-pattern visual hierarchy for P0 KPIs
 * - WCAG 2.1 AA compliant
 * - Mobile-responsive with priority stacking
 * - Micro-interactions on real-time updates
 * - Skeleton loading states
 */

const Executive = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [kpis, setKpis] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [projectHealth, setProjectHealth] = useState(null);
  const [financial, setFinancial] = useState(null);
  const [citiesSummary, setCitiesSummary] = useState(null);
  const [opsHealth, setOpsHealth] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  // Responsive hook for mobile view
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Generate sparkline data (30 days)
  const generateSparklineData = useCallback((baseValue, variance, trend = 'stable') => {
    return Array.from({ length: 30 }, (_, i) => {
      let value = baseValue;
      if (trend === 'up') {
        value += (i / 30) * variance;
      } else if (trend === 'down') {
        value -= (i / 30) * variance;
      }
      value += (Math.random() - 0.5) * variance * 0.3;
      return { value: Math.max(0, value) };
    });
  }, []);

  const fetchData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      // Use Promise.allSettled to handle individual failures gracefully
      const results = await Promise.allSettled([
        executiveAPI.getKPIs(),
        executiveAPI.getAlerts(),
        executiveAPI.getProjectHealth(),
        executiveAPI.getFinancial(),
        stationsAPI.getCitiesSummary(),
        executiveAPI.getOpsHealth(),
      ]);

      // Process each result, only setting state if the promise succeeded
      if (results[0].status === 'fulfilled') {
        setKpis(results[0].value.data.kpis || results[0].value.data);
      }
      if (results[1].status === 'fulfilled') {
        setAlerts(results[1].value.data || { alerts: [] });
      } else {
        setAlerts({ alerts: [] });
      }
      if (results[2].status === 'fulfilled') {
        const phData = results[2].value.data;
        setProjectHealth(phData.phases ? phData : { ...phData, phases: [] });
      } else {
        setProjectHealth({ phases: [] });
      }
      if (results[3].status === 'fulfilled') {
        setFinancial(results[3].value.data);
      }
      if (results[4].status === 'fulfilled') {
        setCitiesSummary(results[4].value.data);
      }
      if (results[5].status === 'fulfilled') {
        const opsData = results[5].value.data;
        // Convert ops health data to expected tiles format if needed
        if (opsData && !opsData.tiles) {
          setOpsHealth({ tiles: [] });
        } else {
          setOpsHealth(opsData);
        }
      } else {
        setOpsHealth({ tiles: [] });
      }
      
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Error fetching executive data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    
    // Auto-refresh every 5 minutes
    const interval = setInterval(() => fetchData(true), 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Loading state with skeleton
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <LogoLockup size="lg" />
            <div>
              <div className="h-8 w-64 bg-gray-200 rounded animate-pulse" />
              <div className="h-4 w-48 bg-gray-200 rounded mt-2 animate-pulse" />
            </div>
          </div>
        </div>
        <DashboardSkeleton widgets={8} />
      </div>
    );
  }

  // Error state
  if (!kpis) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="mx-auto h-12 w-12 text-semantic-warning mb-4" />
        <h2 className="heading-h2 text-gray-900 dark:text-white">Unable to Load Dashboard</h2>
        <p className="text-gray-500 mt-2">Please try refreshing the page.</p>
        <button
          onClick={() => fetchData()}
          className="mt-4 px-6 py-3 bg-urga-electricBlue text-white rounded-xl hover:bg-urga-brightBlue transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  // Mobile view
  if (isMobile) {
    const mobileKpis = [
      { id: 'uptime', title: 'Network Uptime (7d)', value: `${kpis.uptime_percent}%`, delta: '+1.2%', priority: 'p0' },
      { id: 'mttr', title: 'MTTR', value: `${kpis.mttr_hours || 7}h`, delta: '-0.5', priority: 'p0' },
      { id: 'sessions', title: 'Sessions MTD', value: kpis.sessions_mtd.toLocaleString(), delta: '+12%', priority: 'p0' },
      { id: 'revenue', title: 'Revenue MTD', value: `₹${(kpis.revenue_mtd / 100000).toFixed(2)}L`, delta: '+8%', priority: 'p0' },
      { id: 'tickets', title: 'Open High Tickets', value: kpis.open_high_tickets, priority: 'p0' },
      { id: 'util_ac', title: 'Utilization (AC)', value: `${kpis.utilization_ac_90d}%`, priority: 'p1' },
      { id: 'util_dc', title: 'Utilization (DC)', value: `${kpis.utilization_dc_90d}%`, priority: 'p1' },
      { id: 'payouts', title: 'Payouts On-time', value: `${kpis.payouts_ontime_percent}%`, priority: 'p1' },
    ];
    
    return (
      <>
        <SEOHead 
          title="Executive Dashboard"
          description="Real-time executive view of EV charging network operations."
          path="/executive"
          type="WebApplication"
          noIndex={true}
        />
        <MobileExecutiveView
          kpis={mobileKpis}
          onRefresh={() => fetchData(true)}
          isLoading={refreshing}
          lastUpdate={lastUpdate}
        />
      </>
    );
  }

  // Desktop view with Z-pattern layout
  return (
    <>
      <SEOHead 
        title="Executive Dashboard"
        description="Real-time executive view of EV charging network operations. Track KPIs, revenue, network health, and operational metrics for data-driven decision making."
        path="/executive"
        type="WebApplication"
        noIndex={true}
      />
      
      <div data-testid="executive-dashboard" className="space-y-4">
        {/* Compact Header - Clean URGAA branding */}
        <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <h1 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <span style={{ color: '#FF6B35' }}>URGAA</span>
              Executive Dashboard
            </h1>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 flex items-center gap-2">
              Real-time EV infrastructure operations
              <BadgeMadeInBharat size="sm" />
            </p>
          </div>
          
          {/* Actions - Compact */}
          <div className="flex items-center gap-2">
            <button 
              onClick={() => fetchData(true)}
              disabled={refreshing}
              className={cn(
                'px-3 py-2 text-sm font-medium bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700',
                'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700',
                'rounded-lg transition-colors flex items-center gap-1.5 shadow-sm',
                'disabled:opacity-50'
              )}
            >
              <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
              Refresh
            </button>
            <button className="px-3 py-2 text-sm font-medium bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors flex items-center gap-1.5 shadow-sm">
              <Download size={14} />
              Export PDF
            </button>
            <button className="px-3 py-2 text-sm font-medium bg-urga-electricBlue text-white hover:bg-urga-brightBlue rounded-lg transition-colors flex items-center gap-1.5 shadow-sm">
              <FileText size={14} />
              Board Pack
            </button>
          </div>
        </header>

        {/* Ops Health Strip - Real-time status bar */}
        <OpsHealthStrip opsHealth={opsHealth} />

        {/* Summary Section with Square Cards - New Black Design */}
        <SummarySection
          title="Executive Overview"
          subtitle="Real-time infrastructure performance at a glance"
          metrics={[
            {
              label: 'Network Uptime',
              value: `${kpis.uptime_percent}%`,
              icon: Activity,
              subtitle: '7-day average',
              trend: '+1.2%',
              trendDirection: 'up'
            },
            {
              label: 'MTTR',
              value: `${kpis.mttr_hours || 7}h`,
              icon: Timer,
              subtitle: 'Mean time to repair',
              trendDirection: 'up'
            },
            {
              label: 'Sessions MTD',
              value: kpis.sessions_mtd.toLocaleString(),
              icon: Zap,
              trend: '+12%',
              trendDirection: 'up'
            },
            {
              label: 'Revenue MTD',
              value: `₹${(kpis.revenue_mtd / 100000).toFixed(2)}L`,
              icon: DollarSign,
              trend: '+8%',
              trendDirection: 'up'
            },
            {
              label: 'Utilization (AC)',
              value: `${kpis.utilization_ac_90d}%`,
              icon: Gauge,
              subtitle: '90-day avg',
              trendDirection: 'up'
            },
            {
              label: 'High Tickets',
              value: kpis.open_high_tickets,
              icon: AlertTriangle,
              trendDirection: kpis.open_high_tickets > 10 ? 'down' : 'up'
            }
          ]}
        />

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Project Health - Z-pattern (bottom-left) */}
          <GlassCard
            title="Project Health"
            subtitle="PMO rollout progress across 6 phases"
            accentColor="primary"
            definition="Phase completion status"
            source="PMO"
            timestamp="10 mins ago"
            priority="p1"
          >
            {projectHealth && (
              <div className="space-y-4">
                {projectHealth.phases.map((phase, idx) => (
                  <div key={idx} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{phase.name}</span>
                      <div className="flex items-center gap-2">
                        <StatusBadge 
                          status={phase.status === 'completed' ? 'success' : phase.status === 'in_progress' ? 'info' : 'neutral'} 
                          size="xs" 
                          label={phase.status.replace('_', ' ')}
                        />
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {phase.completion}%
                        </span>
                      </div>
                    </div>
                    <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-urga-electricBlue to-brand-green rounded-full transition-all duration-500"
                        style={{ width: `${phase.completion}%` }}
                        role="progressbar"
                        aria-valuenow={phase.completion}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-label={`${phase.name} progress: ${phase.completion}%`}
                      />
                    </div>
                  </div>
                ))}
                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">Slippage:</span>
                    <span className="font-medium text-semantic-warning">
                      {projectHealth.slippage_days} days / ₹{projectHealth.slippage_cost_cr}Cr
                    </span>
                  </div>
                </div>
              </div>
            )}
          </GlassCard>

          {/* Financial Performance - Z-pattern (bottom-right) */}
          <GlassCard
            title="Financial Performance"
            subtitle="P&L Mini Waterfall - Monthly view"
            accentColor="success"
            definition="Revenue to net flow"
            source="Finance"
            timestamp="1 day ago"
            priority="p1"
          >
            {financial && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Gross Revenue</p>
                    <p className="text-xl font-bold text-gray-900 dark:text-white">₹{financial.gross_revenue_cr}Cr</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Net Revenue</p>
                    <p className="text-xl font-bold text-semantic-success">₹{financial.net_revenue_cr}Cr</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Credits Issued</p>
                    <p className="text-xl font-bold text-semantic-danger">-₹{financial.credits_issued_cr}Cr</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-gray-500 dark:text-gray-400">EBITDA %</p>
                    <p className="text-xl font-bold text-gray-900 dark:text-white">{financial.ebitda_percent}%</p>
                  </div>
                </div>
                
                {/* Simple bar visualization */}
                <div className="pt-4">
                  <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-2">
                    <span>Revenue Flow</span>
                  </div>
                  <div className="flex h-4 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700">
                    <div 
                      className="bg-semantic-success transition-all duration-500"
                      style={{ width: `${(financial.net_revenue_cr / financial.gross_revenue_cr) * 100}%` }}
                      aria-label={`Net revenue: ${((financial.net_revenue_cr / financial.gross_revenue_cr) * 100).toFixed(1)}%`}
                    />
                    <div 
                      className="bg-semantic-danger transition-all duration-500"
                      style={{ width: `${(financial.credits_issued_cr / financial.gross_revenue_cr) * 100}%` }}
                      aria-label={`Credits: ${((financial.credits_issued_cr / financial.gross_revenue_cr) * 100).toFixed(1)}%`}
                    />
                  </div>
                </div>
              </div>
            )}
          </GlassCard>
        </div>

        {/* Alerts Section */}
        {alerts && alerts.alerts && alerts.alerts.length > 0 && (
          <GlassCard
            title="Active Alerts"
            subtitle={`${alerts.alerts.length} alerts requiring attention`}
            accentColor="warning"
            definition="Real-time system alerts"
            source="Monitoring"
            timestamp="Live"
            priority="p2"
            collapsible
            defaultExpanded={alerts.alerts.length <= 3}
          >
            <div className="space-y-2" role="log" aria-live="polite">
              {alerts.alerts.slice(0, 5).map((alert, idx) => (
                <div 
                  key={idx} 
                  className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  <StatusBadge 
                    status={alert.severity === 'critical' ? 'danger' : alert.severity === 'warning' ? 'warning' : 'info'} 
                    size="sm" 
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {alert.title}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {alert.timestamp}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </GlassCard>
        )}

        {/* Last Update Footer */}
        <footer className="text-center text-xs text-gray-500 dark:text-gray-400 py-4">
          Last updated: {lastUpdate?.toLocaleTimeString() || 'Never'}
        </footer>
      </div>
    </>
  );
};

export default Executive;
